/** Team-App: API-Hooks, Navigation und Format-Helper. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@vimaana/ui";
import type { TabItem } from "@vimaana/ui";
import type { Member, Saldo, StundenEintrag, Einsatz, OffeneStelle, Kommentar, Rechnung, Projektpreis, Benachrichtigung } from "./types";
export type { Projektpreis, Benachrichtigung } from "./types";

export const APP_NAME = "team";
const BASE = `/api/${APP_NAME}`;

/** Bottom-Tab-Navigation der Team-App. */
export const TEAM_TABS: TabItem[] = [
  { id: "stunden", label: "Stunden", ic: "🕒", to: "/stunden" },
  { id: "einsaetze", label: "Einsätze", ic: "📍", to: "/einsaetze" },
  { id: "stellen", label: "Offene", ic: "📢", to: "/stellen" },
  { id: "profil", label: "Konto", ic: "👤", to: "/profil" },
];

// ── React-Query-Hooks ──────────────────────────────────────────────────────

export function useTeamMe() {
  return useQuery<Member>({
    queryKey: ["team", "me"],
    queryFn: () => apiGet<Member>(`${BASE}/me`),
  });
}

export function useStunden() {
  return useQuery<StundenEintrag[]>({
    queryKey: ["team", "stunden"],
    queryFn: () => apiGet<StundenEintrag[]>(`${BASE}/stunden`),
  });
}

export function useSaldo() {
  return useQuery<Saldo>({
    queryKey: ["team", "saldo"],
    queryFn: () => apiGet<Saldo>(`${BASE}/saldo`),
  });
}

export function useEinsaetze() {
  return useQuery<Einsatz[]>({
    queryKey: ["team", "einsaetze"],
    queryFn: () => apiGet<Einsatz[]>(`${BASE}/einsaetze`),
  });
}

function useEinsatzAntwortInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["team", "einsaetze"] });
    qc.invalidateQueries({ queryKey: ["team", "offene-stellen"] });
    qc.invalidateQueries({ queryKey: ["team", "stunden"] });
  };
}

export function useEinsatzZusagen() {
  const invalidate = useEinsatzAntwortInvalidation();
  return useMutation({
    mutationFn: (slotId: string) => apiPost(`${BASE}/einsaetze/${slotId}/zusagen`, { grund: "" }),
    onSuccess: invalidate,
  });
}

export function useEinsatzAbsagen() {
  const invalidate = useEinsatzAntwortInvalidation();
  return useMutation({
    mutationFn: ({ slotId, grund = "" }: { slotId: string; grund?: string }) =>
      apiPost(`${BASE}/einsaetze/${slotId}/absagen`, { grund }),
    onSuccess: invalidate,
  });
}

export function useOffeneStellen() {
  return useQuery<OffeneStelle[]>({
    queryKey: ["team", "offene-stellen"],
    queryFn: () => apiGet<OffeneStelle[]>(`${BASE}/offene-stellen`),
  });
}

export function useRechnungen() {
  return useQuery<Rechnung[]>({
    queryKey: ["team", "rechnungen"],
    queryFn: () => apiGet<Rechnung[]>(`${BASE}/rechnungen`),
  });
}

export type RechnungUploadResult = {
  id: string; status: string; ki_extrahiert: boolean; betrag: number; datum: string;
};

/** Multipart-Upload (FormData) — apiPost ist JSON-only, daher hier ein eigener
 *  Fetch mit Session-Cookie. Kein Content-Type setzen: der Browser setzt die
 *  multipart-Boundary selbst. */
async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", credentials: "include", body: form });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) { try { parsed = JSON.parse(text); } catch { parsed = text; } }
  if (!res.ok) {
    if (res.status === 401 && typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
    const detail = (parsed && typeof parsed === "object" && "detail" in parsed)
      ? String((parsed as { detail: unknown }).detail) : `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return parsed as T;
}

export type RechnungUploadArgs = {
  datei: File;
  rechnungsart: "stunden" | "pauschale";
  projekt_id?: string;
  rechnungsnummer?: string;
  datum?: string;
  betrag?: number;
  notiz?: string;
};

export function useRechnungUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: RechnungUploadArgs) => {
      const fd = new FormData();
      fd.append("datei", a.datei);
      fd.append("rechnungsart", a.rechnungsart);
      if (a.projekt_id) fd.append("projekt_id", a.projekt_id);
      if (a.rechnungsnummer) fd.append("rechnungsnummer", a.rechnungsnummer);
      if (a.datum) fd.append("datum", a.datum);
      if (a.betrag != null) fd.append("betrag", String(a.betrag));
      if (a.notiz) fd.append("notiz", a.notiz);
      return apiUpload<RechnungUploadResult>(`${BASE}/rechnungen`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", "rechnungen"] });
      qc.invalidateQueries({ queryKey: ["team", "saldo"] });
    },
  });
}

/** URL zum Streamen/Download der hinterlegten Datei einer Rechnung (same-origin →
 *  Session-Cookie wird mitgesendet, daher als einfacher Link nutzbar). */
export function rechnungDateiUrl(belegId: string): string {
  return `${BASE}/rechnungen/${belegId}/datei`;
}

/** Was die KI in einer noch nicht eingereichten Rechnung gelesen hat. */
export type RechnungAnalyse = {
  erkannt: boolean;
  betrag: number;
  datum: string;
  betrag_konfidenz: number;
  datum_konfidenz: number;
};

/** Liest Betrag und Datum aus der gewählten Datei — legt nichts an.
 *
 *  Reine Lesehilfe fürs Formular: schlägt sie fehl, tippt der Helfer wie
 *  bisher selbst. Deshalb wirft die Funktion nicht, sondern liefert ein
 *  leeres Ergebnis. */
export async function rechnungAnalysieren(datei: File): Promise<RechnungAnalyse> {
  const leer: RechnungAnalyse = { erkannt: false, betrag: 0, datum: "", betrag_konfidenz: 0, datum_konfidenz: 0 };
  const fd = new FormData();
  fd.append("datei", datei);
  try {
    const a = await fetch(`${BASE}/rechnungen/analyse`, { method: "POST", body: fd, credentials: "include" });
    if (!a.ok) return leer;
    return (await a.json()) as RechnungAnalyse;
  } catch {
    return leer;
  }
}

export function useRechnungErsetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { belegId: string; datei: File }) => {
      const fd = new FormData();
      fd.append("datei", a.datei);
      return apiUpload<RechnungUploadResult>(`${BASE}/rechnungen/${a.belegId}/ersetzen`, fd);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", "rechnungen"] });
      qc.invalidateQueries({ queryKey: ["team", "saldo"] });
    },
  });
}

/** `slotId` ODER `datum` — beides leer heisst heute. Der Tag bestimmt den
 *  Ordner unter `Verlauf/`; er kommt vom Einsatz, nicht von der Uhr beim
 *  Hochladen (Bilder entstehen abends im Auto). */
export type FotoUploadArgs = {
  projektId: string;
  dateien: File[];
  slotId?: string;
  datum?: string;
};
export type FotoUploadResult = { count: number; ordner: string; arbeitstag: string };

export function useFotoUpload() {
  return useMutation({
    mutationFn: (a: FotoUploadArgs) => {
      const fd = new FormData();
      fd.append("projekt_id", a.projektId);
      if (a.slotId) fd.append("slot_id", a.slotId);
      if (a.datum) fd.append("datum", a.datum);
      a.dateien.forEach(f => fd.append("dateien", f));
      return apiUpload<FotoUploadResult>(`${BASE}/projekt-fotos`, fd);
    },
  });
}

export type LieferscheinArgs = {
  /** Fotos (mehrere Seiten werden zu einem PDF) oder genau ein PDF. */
  dateien: File[];
  projektIds: string[];
  notiz?: string;
};
export type LieferscheinResult = {
  id: string; status: string; projekt_ids: string[]; seiten: number; dateiname: string;
};

/** Lieferschein abgeben. Landet als Beleg-Entwurf im Belegstapel — gelesen
 *  (Lieferant, Positionen) wird er im Büro, nicht auf dem Telefon. */
export function useLieferscheinUpload() {
  return useMutation({
    mutationFn: (a: LieferscheinArgs) => {
      const fd = new FormData();
      a.dateien.forEach(f => fd.append("dateien", f));
      fd.append("projekt_ids", JSON.stringify(a.projektIds));
      if (a.notiz) fd.append("notiz", a.notiz);
      return apiUpload<LieferscheinResult>(`${BASE}/lieferschein`, fd);
    },
  });
}

export function useBewerben() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (terminId: string) => apiPost(`${BASE}/offene-stellen/${terminId}/bewerben`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", "offene-stellen"] });
      qc.invalidateQueries({ queryKey: ["team", "stunden"] });
    },
  });
}

export type ProjektOption = { id: string; title: string };

/**
 * Nachrichten der Bauleitung. Wird beim Fokuswechsel neu geholt — eine Absage
 * soll den Helfer erreichen, wenn er die App aufmacht, nicht erst beim Neuladen.
 */
export function useBenachrichtigungen() {
  return useQuery<Benachrichtigung[]>({
    queryKey: ["team", "benachrichtigungen"],
    queryFn: () => apiGet<Benachrichtigung[]>(`${BASE}/benachrichtigungen`),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export function useBenachrichtigungenGelesen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost(`${BASE}/benachrichtigungen/gelesen`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "benachrichtigungen"] }),
  });
}

export function useProjektpreise() {
  return useQuery<Projektpreis[]>({
    queryKey: ["team", "projektpreise"],
    queryFn: () => apiGet<Projektpreis[]>(`${BASE}/projektpreise`),
  });
}

export function useProjekteOptionen() {
  return useQuery<ProjektOption[]>({
    queryKey: ["team", "projekte"],
    queryFn: () => apiGet<ProjektOption[]>(`${BASE}/projekte`),
  });
}

/** Projekte für den Foto-Upload: alle, an denen der Helfer je eingeteilt war —
 *  auch abgeschlossene. Bewusst NICHT `useProjekteOptionen` (nur aktive): Doku-
 *  Fotos entstehen meist erst, wenn die Baustelle fertig ist. */
/** Projekte für Material: ALLE laufenden Baustellen, nicht nur die eigenen.
 *  Wer beim Händler steht, nimmt auch für eine Baustelle an, auf der er nie
 *  eingeteilt war — deshalb eine eigene Liste neben `useProjekteOptionen`
 *  (Beteiligung, für die Zeiterfassung). */
export function useProjekteAktiv() {
  return useQuery<ProjektOption[]>({
    queryKey: ["team", "projekte-aktiv"],
    queryFn: () => apiGet<ProjektOption[]>(`${BASE}/projekte-aktiv`),
  });
}

export function useProjekteFuerFotos() {
  return useQuery<ProjektOption[]>({
    queryKey: ["team", "projekte-fotos"],
    queryFn: () => apiGet<ProjektOption[]>(`${BASE}/projekte-fotos`),
  });
}

export function useNeueStunden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { projekt_id: string; datum: string; start: string; ende: string;
                        pause_stunden: number; kommentar?: string;
                        verguetungsart?: string; pauschale_id?: string }) =>
      apiPost(`${BASE}/stunden/neu`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team", "stunden"] });
      qc.invalidateQueries({ queryKey: ["team", "saldo"] });
      // Pauschal abgerechnete Zeit haengt an einem Projektpreis — dessen
      // Terminzahl aendert sich damit.
      qc.invalidateQueries({ queryKey: ["team", "projektpreise"] });
    },
  });
}

/** Invalidiert Stunden + Saldo + Einsätze nach einer Slot-Mutation. */
function useSlotInvalidation() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["team", "stunden"] });
    qc.invalidateQueries({ queryKey: ["team", "saldo"] });
    qc.invalidateQueries({ queryKey: ["team", "einsaetze"] });
  };
}

export function useBestaetigen() {
  const invalidate = useSlotInvalidation();
  return useMutation({
    mutationFn: (slotId: string) => apiPost(`${BASE}/stunden/${slotId}/bestaetigen`),
    onSuccess: invalidate,
  });
}

/** Antwort der Slot-Endpunkte (`OkOut` im Webhook). `helfer_status` sagt, ob die
 *  Zeit direkt zählt (`bestaetigt`) oder zur Prüfung liegt (`pending_inhaber`) —
 *  das entscheidet der Server, nicht die App. */
export type SlotAntwort = { confirm_status?: string; helfer_status?: string };

export function useAnpassen() {
  const invalidate = useSlotInvalidation();
  return useMutation({
    mutationFn: ({ slotId, body }: {
      slotId: string;
      body: { start_at: string; end_at: string; pause_stunden: number; kommentar?: string };
    }) => apiPost<SlotAntwort>(`${BASE}/stunden/${slotId}/anpassen`, body),
    onSuccess: invalidate,
  });
}

/** Helfer erfasst Auslagen (Betrag + Notiz) zum eigenen Slot — speichert direkt. */
export function useAuslagen() {
  const invalidate = useSlotInvalidation();
  return useMutation({
    mutationFn: ({ slotId, body }: {
      slotId: string;
      body: { zusatzkosten: number; note: string };
    }) => apiPost(`${BASE}/stunden/${slotId}/auslagen`, body),
    onSuccess: invalidate,
  });
}

export function useKommentieren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slotId, text }: { slotId: string; text: string }) =>
      apiPost(`${BASE}/stunden/${slotId}/kommentar`, { text }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["team", "kommentare", v.slotId] }),
  });
}

export function useKommentare(slotId: string, enabled: boolean) {
  return useQuery<Kommentar[]>({
    queryKey: ["team", "kommentare", slotId],
    queryFn: () => apiGet<Kommentar[]>(`${BASE}/stunden/${slotId}/kommentare`),
    enabled,
  });
}

// ── Format-Helper ────────────────────────────────────────────────────────────

const DAY = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export function fmtEUR(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export function fmtHours(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + " h";
}

/** ISO-Timestamp → "Mo, 02.06." */
export function fmtTag(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${DAY[d.getDay()]}, ${dd}.${mm}.`;
}

/** ISO-Timestamp → "08:00". */
export function fmtZeit(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    const m = iso.match(/T(\d{2}):(\d{2})/);
    return m ? `${m[1]}:${m[2]}` : "";
  }
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function statusLabel(s: string): string {
  if (s === "bestaetigt") return "bestätigt";
  if (s === "pending_inhaber") return "wartet auf Inhaber";
  return "warten";
}

/** Lokales Datum (YYYY-MM-DD) aus einem ISO-Timestamp. */
export function localDateIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Baut einen (zeitzonenlosen, lokalen) ISO-Timestamp wie der Planer: "YYYY-MM-DDTHH:MM:00". */
export function combineLocal(dateIso: string, hhmm: string): string {
  return `${dateIso}T${hhmm}:00`;
}

// ─────────────────────────── Einsatz-Checkliste (Welle 4) ──────────────────

export type Aufgabe = {
  id: string;
  titel: string;
  sort_order: number;
  erledigt_am: string | null;
  erledigt_von: string | null;
  notiz: string;
};

export function useAufgaben(terminId: string | null) {
  return useQuery<Aufgabe[]>({
    queryKey: ["team", "aufgaben", terminId],
    queryFn: () => apiGet<Aufgabe[]>(`${BASE}/einsatz/${terminId}/aufgaben`),
    enabled: !!terminId,
  });
}

export function useAufgabeToggle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aufgabeId: string) =>
      apiPost<Aufgabe>(`${BASE}/aufgabe/${aufgabeId}/toggle`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team", "aufgaben"] }),
  });
}
